import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ADMIN_PATHS, adminPath } from '../../constants/adminPaths';
import { adminListAppUsers, adminUpdateAppUser, toUserDate } from '../../api/appUsersApi';
import UserProfileDialog from './UserProfileDialog';

const RecentSignups = () => {
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [clearingUser, setClearingUser] = useState(null);

  useEffect(() => {
    const fetchRecentSignups = async () => {
      try {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const { users } = await adminListAppUsers({
          role: 'owner',
          accountType: 'company',
          createdAfter: thirtyDaysAgo.toISOString(),
          uncleared: true,
          limit: 10,
        });
        setRecentUsers(users.slice(0, 3));
      } catch (error) {
        console.error('Error fetching recent signups:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentSignups();
  }, []);

  const handleUserClick = (user) => setSelectedUser(user);
  const handleCloseDialog = () => setSelectedUser(null);

  const handleClearUser = async (userId) => {
    setClearingUser(userId);
    try {
      await adminUpdateAppUser(userId, { cleared: true });
      setRecentUsers((users) => users.filter((u) => u.id !== userId));
    } catch (error) {
      console.error('Error clearing user:', error);
    } finally {
      setClearingUser(null);
    }
  };

  const formatDate = (timestamp) => {
    const date = toUserDate(timestamp);
    if (!date) return '';
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="dash-card__body">
      <div className="dash-card__header">
        <h3 className="dash-card__title">Recent Signups</h3>
        <RouterLink to={adminPath(ADMIN_PATHS.SIGNUPS)} className="dash-card__link">View All</RouterLink>
      </div>

      {loading ? (
        <div>
          {[0,1,2].map((i) => (
            <div key={i} className="dash-row dash-row--skeleton">
              <div className="skeleton" style={{ width: 160 }} />
              <div className="skeleton" style={{ width: 100 }} />
            </div>
          ))}
        </div>
      ) : recentUsers.length === 0 ? (
        <div className="dash-card__empty">No new signups to review.</div>
      ) : (
        <div>
          {recentUsers.map((user, index) => (
            <React.Fragment key={user.id}>
              <div className="dash-row" onClick={() => handleUserClick(user)} style={{ cursor: 'pointer' }}>
                <div className="dash-row__left">
                  <div className="dash-row__title">{user.firstName} {user.lastName}</div>
                  <div className="dash-row__subtitle">{user.companyName}</div>
                </div>
                <div className="dash-row__right">
                  <div className="dash-row__meta">{formatDate(user.createdAt)}</div>
                  <button
                    className="dash-icon-btn"
                    onClick={(e) => { e.stopPropagation(); handleClearUser(user.id); }}
                    disabled={clearingUser === user.id}
                    aria-label="Clear signup"
                    title="Clear signup"
                  >
                    {clearingUser === user.id ? <div className="dash-spinner" /> : <span className="dash-icon" />}
                  </button>
                </div>
              </div>
              {index < recentUsers.length - 1 && (
                <div style={{ borderTop: '1px solid rgba(10, 37, 64, 0.12)' }} />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      <UserProfileDialog 
        open={Boolean(selectedUser)}
        onClose={handleCloseDialog}
        user={selectedUser}
      />
    </div>
  );
};

export default RecentSignups; 